import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Package } from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { PageTransition } from '../../components/animations/PageTransition';
import { RichTextEditor } from '../../components/editor';
import { useUIStore } from '../../stores/uiStore';

interface Project {
  id: string;
  name: string;
  organizationId: string;
}

export function NewBundlePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setSidebarCollapsed } = useUIStore();

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    projectId: searchParams.get('projectId') || '',
    title: '',
    descriptionHtml: '',
  });

  // Collapse sidebar when editing
  useEffect(() => {
    setSidebarCollapsed(true);
    return () => setSidebarCollapsed(false);
  }, [setSidebarCollapsed]);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await api.get<Project[]>('/projects');
      setProjects(response.data || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const handleSubmit = async () => {
    if (!form.projectId || !form.title) return;

    setLoading(true);
    try {
      const response = await api.post<{ id: string }>('/bundles', {
        projectId: form.projectId,
        title: form.title,
        descriptionHtml: form.descriptionHtml || null,
      });
      navigate(`/bundles/${response.data?.id}`);
    } catch (error) {
      console.error('Failed to create bundle:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTransition>
      <div className="flex-1 flex flex-col min-h-0">
        {/* Header Bar */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-xl hover:bg-bg-secondary transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-accent/10">
                <Package className="w-5 h-5 text-accent" />
              </div>
              <h1 className="text-xl font-bold">New Bundle</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              isLoading={loading}
              disabled={!form.projectId || !form.title}
            >
              Create Bundle
            </Button>
          </div>
        </div>

        {/* Form */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-6 gap-6 min-h-0">
          {/* Left Column - Title + Editor (5/6) */}
          <div className="lg:col-span-5 flex flex-col min-h-0 order-2 lg:order-1">
            {/* Title */}
            <div className="mb-3">
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Bundle title..."
                className="w-full px-4 py-3 bg-bg-secondary border border-border rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-accent/50"
              />
            </div>
            {/* Editor */}
            <div className="flex-1 min-h-0">
              <RichTextEditor
                content={form.descriptionHtml}
                onChange={(html) => setForm({ ...form, descriptionHtml: html })}
                placeholder="Describe the bundle deliverables. You can add work items after creating the bundle."
                className="h-full"
              />
            </div>
          </div>

          {/* Right Column - Settings (1/6) */}
          <div className="lg:col-span-1 space-y-4 order-1 lg:order-2">
            {/* Project */}
            <div>
              <label className="block text-sm font-medium mb-2">Project *</label>
              <select
                value={form.projectId}
                onChange={(e) => setForm({ ...form, projectId: e.target.value })}
                className="w-full px-3 py-2.5 bg-bg-secondary border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent/50"
              >
                <option value="">Select project</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Info */}
            <div className="p-4 bg-accent/10 border border-accent/20 rounded-xl">
              <Package className="w-5 h-5 text-accent mb-2" />
              <p className="text-sm text-text-secondary">
                Bundles group related work items for complex deliverables. After creating the bundle, you can add individual work items to it.
              </p>
            </div>
          </div>
        </div>
      </div>
    </PageTransition>
  );
}
